import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import chatApi from '../../../api/chatApi';
import { PromptSuggestion } from '../../../api/types';

interface PromptLibraryModalProps {
    onClose: () => void;
    onSelect: (prompt: string) => void;
}

const PromptLibraryModal: React.FC<PromptLibraryModalProps> = ({ onClose, onSelect }) => {
    const [prompts, setPrompts] = useState<PromptSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<string>('');
    const [categories, setCategories] = useState<string[]>([]);

    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                const data = await chatApi.getPrompts();
                const safeData = data || [];
                setPrompts(safeData);

                // 1. Extract unique categories dynamically
                const uniqueCategories = Array.from(
                    new Set(safeData.map((p) => p.category || 'General'))
                );

                setCategories(uniqueCategories);

                // 2. Set the first category as active by default
                if (uniqueCategories.length > 0) {
                    setActiveTab(uniqueCategories[0]);
                }
            } catch (error) {
                console.error("Failed to load prompts:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPrompts();
    }, []);

    // 3. Filter prompts based on the dynamically selected tab
    const filteredPrompts = useMemo(() => {
        return prompts.filter(p =>
            (p.category || 'General') === activeTab
        );
    }, [prompts, activeTab]);

    return (
        // OVERLAY: Responsive padding (p-4 on desktop, p-2 on mobile)
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 sm:p-2">

            {/* MODAL BOX: Fixed 520x480 on desktop, fluid on mobile */}
            <div
                className="bg-white relative flex flex-col shadow-2xl overflow-hidden rounded-xl w-full max-w-[520px] h-[480px] max-h-[90vh]"
            >
                {/* Close Button */}
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-50 p-1 hover:bg-gray-100 rounded-md transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header Section */}
                <div className="pt-6 px-6 pb-2 shrink-0 bg-white z-10">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Prompt Library</h2>

                    {/* Dynamic Tabs */}
                    <div className="flex items-center gap-6 border-b border-gray-200 overflow-x-auto no-scrollbar">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveTab(category)}
                                className={`text-sm font-medium pb-2 transition-colors relative whitespace-nowrap ${activeTab === category
                                    ? 'text-black border-b-2 border-[#A497FF]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scrollable Prompts List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">Loading...</span>
                        </div>
                    ) : filteredPrompts.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                            <p>No prompts found.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredPrompts.map((item, index) => (
                                <button
                                    key={item.id || index}
                                    onClick={() => onSelect(item.question)}
                                    className="flex items-center w-full text-left p-3 px-4 border border-gray-200 rounded-lg hover:border-[#7C3AED] hover:bg-purple-50/30 transition-all group"
                                >
                                    {/* Added Numbering Here */}
                                    <span className="text-[14px] text-black select-none">
                                        {index + 1}.
                                    </span>
                                    <span className="ml-2"> </span>
                                    <p className="text-[14px] leading-relaxed text-black group-hover:text-gray-900">
                                        {item.question}
                                    </p>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default PromptLibraryModal;